import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { scrollToId, scrollToTop } from "../../lib/scroll";

const HOME_LINKS = [
  { label: "Menu", href: "#menu" },
  { label: "Craft", href: "#manifesto" },
  { label: "Enquire", href: "#enquire" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isCatalogue = location.pathname === "/catalogue";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (!isHome) {
      // Navigate home first, then scroll after paint
      navigate("/");
      setTimeout(() => scrollToId(href), 120);
    } else {
      scrollToId(href);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => scrollToTop(), 60);
    } else {
      scrollToTop();
    }
  };

  const goCatalogue = () => {
    setOpen(false);
    navigate("/catalogue");
    window.scrollTo({ top: 0 });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || isCatalogue
          ? "bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E6E2D8]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={goHome}
          className="font-display text-2xl md:text-3xl tracking-tight leading-none text-[#2A221C]"
        >
          <img src="/logo.svg" width={"110px"} alt="Artisan Bites" />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {HOME_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-xs tracking-[0.2em] uppercase font-medium text-[#2A221C]/80 hover:text-[#D4A373] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={goCatalogue}
            className={`text-xs tracking-[0.2em] uppercase font-medium transition-colors ${
              isCatalogue ? "text-[#D4A373]" : "text-[#2A221C]/80 hover:text-[#D4A373]"
            }`}
          >
            Catalogue
          </button>
          <button
            data-testid="nav-cta"
            onClick={() => go("#enquire")}
            className="text-xs tracking-[0.2em] uppercase font-medium border border-[#2A221C] px-5 py-2.5 hover:bg-[#2A221C] hover:text-[#FDFBF7] transition-colors"
          >
            Request Samples
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden flex flex-col gap-[5px] p-2"
          aria-label="Toggle menu"
        >
          <span className={`h-[2px] w-6 bg-[#2A221C] transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
          <span className={`h-[2px] w-6 bg-[#2A221C] transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`h-[2px] w-6 bg-[#2A221C] transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#FDFBF7] border-b border-[#E6E2D8]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {HOME_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-3xl text-[#2A221C]"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={goCatalogue}
                className={`text-left font-display text-3xl ${isCatalogue ? "text-[#D4A373]" : "text-[#2A221C]"}`}
              >
                Catalogue
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
